// TelcoTrack — toast queue without Angular so timing can be unit-tested.
// Feature copy is stored unchanged. One dismiss duration for every variant.

import { BehaviorSubject } from 'rxjs';
import { prefersReducedMotion } from '../../../utils/overlay-motion.util';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  /** Caller string, including the original Czech copy. */
  message: string;
  leaving: boolean;
}

/** Enter and leave. Within the 150–200ms ease-out window. */
export const TT_TOAST_MOTION_MS = 180;

/** One auto-dismiss for every variant (success / error / info). */
export const TT_TOAST_DISMISS_MS = 5000;

export class ToastQueue {
  private readonly itemsSubject = new BehaviorSubject<ToastItem[]>([]);
  readonly items$ = this.itemsSubject.asObservable();

  private nextId = 1;
  private readonly dismissTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly leaveTimers = new Map<number, ReturnType<typeof setTimeout>>();

  destroy(): void {
    this.dismissAllImmediate();
  }

  showSuccess(message: string): void {
    this.enqueue('success', message);
  }

  showError(message: string): void {
    this.enqueue('error', message);
  }

  showInfo(message: string): void {
    this.enqueue('info', message);
  }

  /** Drop every toast immediately (no leave animation). Used when replacing a prior hint. */
  dismissAll(): void {
    this.dismissAllImmediate();
  }

  dismiss(id: number): void {
    this.clearTimer(this.dismissTimers, id);
    const current = this.itemsSubject.value;
    const item = current.find((entry) => entry.id === id);
    if (!item || item.leaving) return;

    if (prefersReducedMotion()) {
      this.remove(id);
      return;
    }

    this.itemsSubject.next(
      current.map((entry) => (entry.id === id ? { ...entry, leaving: true } : entry)),
    );
    const timer = setTimeout(() => {
      this.leaveTimers.delete(id);
      this.remove(id);
    }, TT_TOAST_MOTION_MS);
    this.leaveTimers.set(id, timer);
  }

  private dismissAllImmediate(): void {
    for (const timer of this.dismissTimers.values()) clearTimeout(timer);
    for (const timer of this.leaveTimers.values()) clearTimeout(timer);
    this.dismissTimers.clear();
    this.leaveTimers.clear();
    this.itemsSubject.next([]);
  }

  private enqueue(variant: ToastVariant, message: string): void {
    if (typeof message !== 'string' || message.trim() === '') return;
    const id = this.nextId++;
    const item: ToastItem = { id, variant, message, leaving: false };
    this.itemsSubject.next([...this.itemsSubject.value, item]);
    const timer = setTimeout(() => {
      this.dismissTimers.delete(id);
      this.dismiss(id);
    }, TT_TOAST_DISMISS_MS);
    this.dismissTimers.set(id, timer);
  }

  private remove(id: number): void {
    this.clearTimer(this.dismissTimers, id);
    this.clearTimer(this.leaveTimers, id);
    this.itemsSubject.next(this.itemsSubject.value.filter((entry) => entry.id !== id));
  }

  private clearTimer(timers: Map<number, ReturnType<typeof setTimeout>>, id: number): void {
    const timer = timers.get(id);
    if (timer == null) return;
    clearTimeout(timer);
    timers.delete(id);
  }
}
